/*
 * Copyright (c) 2026. Orion Analytics.
 * All Rights Reserved.
 */

import React, { useState } from "react";
import { HelpCircle, X } from "lucide-react";

import metricDefinitions from "../../data/metricDefinitions.json";
import { metricRegistry } from "./MetricRegistry";

export default function MetricAssistant({

    context = "market",
    title = "Metric Assistant"

}) {

    const [selectedMetric, setSelectedMetric] =
        useState(null);

    const metrics =
        metricRegistry[context] || {};

    const metricData =
        selectedMetric
            ? metrics[selectedMetric]
            : null;

    const metricKeys = Object.keys(metrics);

    return (

        <>
            {/* ============================================
                FLOATING PILL PANEL
            ============================================ */}

            <div
                className="
                    hidden
                    xl:block
                    fixed
                    right-6
                    top-40
                    z-40
                "
            >

                <div
                    className="
                        bg-slate-900/55
                        backdrop-blur-xl
                        border
                        border-slate-700
                        rounded-3xl
                        shadow-2xl
                        p-4
                        w-64
                    "
                >

                    <div
                        className="
                            flex
                            items-center
                            gap-2
                            mb-4
                        "
                    >
                        <HelpCircle
                            size={18}
                            className="text-cyan-400"
                        />

                        <h3
                            className="
                                text-sm
                                font-semibold
                                text-slate-100
                            "
                        >
                            {title}
                        </h3>
                    </div>

                    <div
                        className="
                            flex
                            flex-wrap
                            gap-2
                        "
                    >

                        {metricKeys.map(metric => (

                            <button
                                key={metric}
                                onClick={() =>
                                    setSelectedMetric(metric)
                                }
                                className="
                                    px-3
                                    py-1.5
                                    text-xs
                                    rounded-full
                                    bg-slate-800
                                    border
                                    border-slate-700
                                    text-slate-300
                                    hover:bg-cyan-600
                                    hover:border-cyan-500
                                    hover:text-white
                                    transition-all
                                    duration-200
                                "
                            >
                                {metrics[metric].title}
                            </button>

                        ))}

                    </div>

                </div>

            </div>

            {/* ============================================
                SLIDE OUT INFO PANEL
            ============================================ */}

            <div
                className={`
                    fixed
                    top-0
                    right-0
                    h-screen
                    w-[480px]
                    z-50
                    transform
                    transition-transform
                    duration-300
                    ease-in-out
                    ${
                        metricData
                            ? "translate-x-0"
                            : "translate-x-full"
                    }
                `}
            >

                <div
                    className="
                        h-full
                        bg-slate-950
                        border-l
                        border-slate-800
                        shadow-2xl
                        overflow-y-auto
                    "
                >

                    {metricData && (

                        <>

                            {/* HEADER */}

                            <div
                                className="
                                    sticky
                                    top-0
                                    z-10
                                    bg-slate-950
                                    border-b
                                    border-slate-800
                                    px-6
                                    py-5
                                    flex
                                    justify-between
                                    items-center
                                "
                            >

                                <div>

                                    <div
                                        className="
                                            text-xs
                                            uppercase
                                            tracking-wider
                                            text-cyan-400
                                        "
                                    >
                                        {
                                            metricData.category
                                        }
                                    </div>

                                    <h2
                                        className="
                                            text-xl
                                            font-bold
                                            text-white
                                            mt-1
                                        "
                                    >
                                        {
                                            metricData.title
                                        }
                                    </h2>

                                </div>

                                <button
                                    onClick={() =>
                                        setSelectedMetric(null)
                                    }
                                    className="
                                        p-2
                                        rounded-full
                                        bg-slate-800
                                        hover:bg-red-500
                                        text-slate-300
                                        hover:text-white
                                        transition
                                    "
                                >
                                    <X size={18}/>
                                </button>

                            </div>

                            {/* CONTENT */}

                            <div className="p-6 space-y-6">

                                {/* Description */}

                                <section>

                                    <h3
                                        className="
                                            text-cyan-400
                                            font-semibold
                                            mb-2
                                        "
                                    >
                                        What Is It?
                                    </h3>

                                    <p
                                        className="
                                            text-slate-300
                                            leading-relaxed
                                        "
                                    >
                                        {
                                            metricData.description
                                        }
                                    </p>

                                </section>

                                {/* Why Important */}

                                {metricData.whyItMatters && (

                                    <section>

                                        <h3
                                            className="
                                                text-cyan-400
                                                font-semibold
                                                mb-2
                                            "
                                        >
                                            Why It Matters
                                        </h3>

                                        <p
                                            className="
                                                text-slate-300
                                                leading-relaxed
                                            "
                                        >
                                            {
                                                metricData.whyItMatters
                                            }
                                        </p>

                                    </section>

                                )}

                                {/* Interpretation */}

                                {metricData.interpretation && (

                                    <section>

                                        <h3
                                            className="
                                                text-cyan-400
                                                font-semibold
                                                mb-3
                                            "
                                        >
                                            Interpretation
                                        </h3>

                                        <div className="space-y-3">

                                            {metricData.interpretation.map(
                                                (item, idx) => (

                                                    <div
                                                        key={idx}
                                                        className="
                                                            bg-slate-900
                                                            border
                                                            border-slate-800
                                                            rounded-xl
                                                            p-3
                                                        "
                                                    >

                                                        <div
                                                            className="
                                                                text-cyan-300
                                                                font-medium
                                                            "
                                                        >
                                                            {item.range}
                                                        </div>

                                                        <div
                                                            className="
                                                                text-slate-300
                                                                text-sm
                                                                mt-1
                                                            "
                                                        >
                                                            {item.meaning}
                                                        </div>

                                                    </div>

                                                )
                                            )}

                                        </div>

                                    </section>

                                )}

                                {/* Strategy */}

                                {metricData.strategy && (

                                    <section>

                                        <h3
                                            className="
                                                text-cyan-400
                                                font-semibold
                                                mb-3
                                            "
                                        >
                                            Trading Applications
                                        </h3>

                                        <ul
                                            className="
                                                list-disc
                                                pl-5
                                                text-slate-300
                                                space-y-2
                                            "
                                        >

                                            {metricData.strategy.map(
                                                (item, idx) => (

                                                    <li key={idx}>
                                                        {item}
                                                    </li>

                                                )
                                            )}

                                        </ul>

                                    </section>

                                )}

                                {/* Pro Notes */}

                                {metricData.proNotes && (

                                    <section>

                                        <div
                                            className="
                                                bg-cyan-950/40
                                                border
                                                border-cyan-800
                                                rounded-xl
                                                p-4
                                            "
                                        >

                                            <h3
                                                className="
                                                    text-cyan-300
                                                    font-semibold
                                                    mb-2
                                                "
                                            >
                                                Professional Insight
                                            </h3>

                                            <p
                                                className="
                                                    text-slate-300
                                                    text-sm
                                                    leading-relaxed
                                                "
                                            >
                                                {
                                                    metricData.proNotes
                                                }
                                            </p>

                                        </div>

                                    </section>

                                )}

                            </div>

                        </>

                    )}

                </div>

            </div>
        </>

    );

}